/**
 * workshopDetailUrlFor — builds the link to the workshop detail page (BON-117).
 *
 * The route lives at `workshops/:workshopId` under each mode's tree, so the
 * absolute path depends on which mode the caller is in:
 *
 *   admin   → /admin/workshops/:id
 *   teacher → /me/workshops/:id   (read-only)
 *
 * There used to be a third, region-scoped shape. The regional tree is gone —
 * every admin page scopes itself by query parameter now — so a workshop has
 * one address per mode and the mode is the whole of the shell.
 *
 * `shellBasePath` returns the tree root (used to build the breadcrumb's Venue
 * link from inside the detail page, which only knows its own location).
 * `workshopsListUrlFor` gives the breadcrumb's Workshops link. Mirrors the
 * venue-detail convention.
 */
import { MODE_ROOT, modeFromPath, type ViewMode } from '../../shared/nav/viewMode';

import { TAB_SLUGS } from './venues/detail/venue-tabs';

/** Which tree the detail page is mounted in. */
export type WorkshopShell = ViewMode;

/** Root path of a mode's tree (no trailing slash). */
export function shellBasePath(shell: WorkshopShell): string {
  return MODE_ROOT[shell];
}

export function workshopDetailUrlFor(shell: WorkshopShell, workshopId: string): string {
  return `${shellBasePath(shell)}/workshops/${encodeURIComponent(workshopId)}`;
}

/**
 * Where the detail page's "Workshops" link goes. Neither mode has a workshops
 * list page, so admin goes to the venue's Workshops tab and a teacher, who has
 * no venue pages, goes to My schedule. A workshop with no venue is a private
 * event, so admin goes to the private events list instead.
 */
export function workshopsListUrlFor(shell: WorkshopShell, venueId: string | null): string {
  if (shell === 'teacher') return `${MODE_ROOT.teacher}/schedule`;
  if (venueId === null) return `${MODE_ROOT.admin}/private-events`;
  return `${MODE_ROOT.admin}/venues/${encodeURIComponent(venueId)}/${TAB_SLUGS.Workshops}`;
}

/**
 * Detail URL for a workshop chip clicked on an admin surface. A private event
 * shares its workshop's id but has its own detail page, so it opens there
 * rather than on the public workshop page.
 */
export function adminChipDetailUrl(chip: { id: string; isPrivate?: boolean }): string {
  if (chip.isPrivate) return `${MODE_ROOT.admin}/private-events/${encodeURIComponent(chip.id)}`;
  return workshopDetailUrlFor('admin', chip.id);
}

/**
 * Detail URL for a workshop chip clicked on a TEACHER calendar. BON-238: the
 * detail endpoint is access-guarded and a teacher has no admin-mode access, so
 * every teacher chip routes inside the teacher tree. A chip for a workshop the
 * teacher isn't assigned to renders the detail page's not-found state (the API
 * denies with 404, per the read guard). A private event opens on the teacher's
 * copy of its own page. The `regionSlug` field stays accepted (and ignored) so
 * chip callers need no change.
 */
export function teacherChipDetailUrl(chip: {
  id: string;
  regionSlug?: string | null;
  isPrivate?: boolean;
}): string {
  if (chip.isPrivate) return `${MODE_ROOT.teacher}/private-events/${encodeURIComponent(chip.id)}`;
  return workshopDetailUrlFor('teacher', chip.id);
}

/** Detail URL for a workshop clicked in either tree. Both open a private event
 *  on its own page, inside the caller's own tree. */
export function chipDetailUrlFor(
  shell: WorkshopShell,
  chip: { id: string; isPrivate?: boolean },
): string {
  return shell === 'admin' ? adminChipDetailUrl(chip) : teacherChipDetailUrl(chip);
}

/**
 * Derive the current tree's base path from a location pathname, for components
 * that are mounted inside one but don't receive the mode as a prop (e.g. the
 * venue Workshops tab table). Defaults to `/admin`.
 *
 *   /admin/venues/123 → /admin
 *   /me/schedule      → /me
 */
export function shellBasePathFromPathname(pathname: string): string {
  return shellBasePath(workshopShellFromPathname(pathname));
}

/**
 * Derive the {@link WorkshopShell} from a location pathname — for components
 * mounted inside a tree that need it (e.g. to build a post-create redirect)
 * but don't receive it as a prop. Defaults to admin.
 */
export function workshopShellFromPathname(pathname: string): WorkshopShell {
  return modeFromPath(pathname) ?? 'admin';
}
